import { ReactElement, useState, useEffect, useRef } from 'react';
import { useTranslation } from 'next-i18next';
import Button from '@Hotels-at-Home/hah-fe-core/dist/components/Button';
import { Link } from '@Hotels-at-Home/hah-fe-core/dist/components';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination, A11y, Controller } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/pagination';
import useSwiperRef from '~/ui/components/common/Swiper/useSwiperRef';

const CollaborationsSection = (): ReactElement => {
  const { t } = useTranslation('', { keyPrefix: '$pages.$home.$collaborationsSection' });
  const { t: tButton } = useTranslation('', { keyPrefix: '$common.$buttons' });
  const { t: tLink } = useTranslation('', {
    keyPrefix: '$local.$homePageLinks.$collaborationSection',
  });

  const [nextEl, nextElRef] = useSwiperRef();
  const [prevEl, prevElRef] = useSwiperRef();

  const paginationRef = useRef(null);

  const [swiperInstance1, setSwiperInstance1] = useState(null);
  const [swiperInstance2, setSwiperInstance2] = useState(null);

  useEffect(() => {
    if (swiperInstance1 && paginationRef.current) {
      swiperInstance1.params.pagination.el = paginationRef.current;

      swiperInstance1.pagination.destroy();
      swiperInstance1.pagination.init();
      swiperInstance1.pagination.update();
    }
  }, [swiperInstance1]);

  return (
    <section
      id="hp-collaborations"
      className="hp-section collaborations-section grey collaborations"
    >
      <h2 className="sr-only">{t('title')}</h2>
      <h2 className="mobile-only">{t('title')}</h2>
      <div className="carousel-wrapper">
        <div id="hp-swiper-collaborations-1" className="swiper">
          <Swiper
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            slidesPerView={1}
            navigation={{
              prevEl,
              nextEl,
            }}
            pagination={{
              el: paginationRef.current,
              type: 'fraction',
              renderFraction: (currentClass, totalClass) =>
                `<span class="${currentClass}"></span>&nbsp;/&nbsp;<span class="${totalClass}"></span>`,
            }}
            containerModifierClass="swiper-container-"
            className="swiper-container swiper-wrapper"
            modules={[Navigation, Pagination, A11y, Controller]}
            onSwiper={setSwiperInstance1}
            controller={{ control: swiperInstance2 }}
          >
            <SwiperSlide className="swiper-slide">
              <Link
                href={tLink('bernardaud')}
                className="clearfix"
                data-event-name="bloc_interact"
                data-bloc-name={t('firstSlideTitle')}
              >
                <div className="stack">
                  <h3>{t('firstSlideTitle')}</h3>
                  <p className="desktop-only">{t('firstSlideDescriptionDesktop')}</p>
                  <p className="mobile-only">{t('firstSlideDescriptionMobile')}</p>
                  <span className="btn base-btn btn-plain">
                    <span>{tButton('shopNow')}</span>
                  </span>
                </div>
              </Link>
            </SwiperSlide>
            <SwiperSlide className="swiper-slide">
              <Link
                href={tLink('capdarsene')}
                className="clearfix"
                data-event-name="bloc_interact"
                data-bloc-name={t('thirdSlideTitle')}
              >
                <div className="stack">
                  <h3>{t('secondSlideTitle')}</h3>
                  <p className="desktop-only">{t('secondSlideDescriptionDesktop')}</p>
                  <p className="mobile-only">{t('secondSlideDescriptionMobile')}</p>
                  <span className="btn base-btn btn-plain">
                    <span>{tButton('shopNow')}</span>
                  </span>
                </div>
              </Link>
            </SwiperSlide>
            <SwiperSlide className="swiper-slide">
              <Link
                href={tLink('truetribe')}
                className="clearfix"
                data-event-name="bloc_interact"
                data-bloc-name={t('fourthSlideTitle')}
              >
                <div className="stack">
                  <h3>{t('thirdSlideTitle')}</h3>
                  <p className="desktop-only">{t('thirdSlideDescriptionDesktop')}</p>
                  <p className="mobile-only">{t('thirdSlideDescriptionMobile')}</p>
                  <span className="btn base-btn btn-plain">
                    <span>{tButton('shopNow')}</span>
                  </span>
                </div>
              </Link>
            </SwiperSlide>
          </Swiper>
          <div className="swiper-actions swiper-buttons">
            <button
              id="collaborations-slide-prev"
              className="swiper-button swiper-button-prev"
              type="button"
              aria-label="Previous button is disabled"
              ref={prevElRef}
            >
              <span className="icon icon-arrow" />
            </button>
            <div className="carousel-pages" ref={paginationRef} />
            <button
              id="collaborations-slide-next"
              className="swiper-button swiper-button-next"
              type="button"
              aria-label="Next, go to the 3/4 slide"
              ref={nextElRef}
            >
              <span className="icon icon-arrow" />
            </button>
          </div>
        </div>
        <div id="hp-swiper-collaborations-2" className="swiper">
          <Swiper
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            slidesPerView={1}
            containerModifierClass="swiper-container-"
            className="swiper-container swiper-wrapper"
            modules={[A11y, Controller]}
            onSwiper={setSwiperInstance2}
            controller={{ control: swiperInstance1 }}
          >
            <SwiperSlide className="swiper-slide">
              <Link
                href={tLink('bernardaud')}
                className="clearfix"
                data-event-name="bloc_interact"
                data-bloc-name={t('firstSlideTitle')}
              >
                <picture>
                  <source
                    data-srcset="images/homePage/banners/hp-bernardaud-lamp.webp"
                    media="(max-width: 768px)"
                    srcSet="images/homePage/banners/hp-bernardaud-lamp.webp"
                  />
                  <img
                    loading="lazy"
                    data-src="images/homePage/banners/hp-bernardaud-lamp.webp"
                    alt={t('firstSlideTitle')}
                    src="images/homePage/banners/hp-bernardaud-lamp.webp"
                    width="1000"
                    height="1022"
                  />
                </picture>
              </Link>
            </SwiperSlide>
            <SwiperSlide className="swiper-slide">
              <Link
                href={tLink('capdarsene')}
                className="clearfix"
                data-event-name="bloc_interact"
                data-bloc-name={t('thirdSlideTitle')}
              >
                <picture>
                  <source
                    data-srcset="images/homePage/banners/hp-cap-d-arsene.webp"
                    media="(max-width: 768px)"
                    srcSet="images/homePage/banners/hp-cap-d-arsene.webp"
                  />
                  <img
                    loading="lazy"
                    data-src="images/homePage/banners/hp-cap-d-arsene.webp"
                    alt={t('thirdSlideTitle')}
                    src="images/homePage/banners/hp-cap-d-arsene.webp"
                    width="1000"
                    height="1022"
                  />
                </picture>
              </Link>
            </SwiperSlide>
            <SwiperSlide className="swiper-slide">
              <Link
                href={tLink('truetribe')}
                className="clearfix"
                data-event-name="bloc_interact"
                data-bloc-name={t('fourthSlideTitle')}
              >
                <picture>
                  <source
                    data-srcset="images/homePage/banners/hp-true-tribe.webp"
                    media="(max-width: 768px)"
                    srcSet="images/homePage/banners/hp-true-tribe.webp"
                  />
                  <img
                    loading="lazy"
                    data-src="images/homePage/banners/hp-true-tribe.webp"
                    alt={t('fourthSlideTitle')}
                    src="images/homePage/banners/hp-true-tribe.webp"
                    width="1000"
                    height="1022"
                  />
                </picture>
              </Link>
            </SwiperSlide>
          </Swiper>
        </div>
        {/* More stacks should go in here for the carousel later */}
      </div>
    </section>
  );
};

export default CollaborationsSection;
